import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LABEL_STOCK } from '../lib/assets';
import '../components/Assets.css';

/**
 * Printable QR labels for the devices chosen on the list.
 *
 * Laid out in millimetres for the label stock IT has, so what comes out of
 * the printer lands on the labels rather than across them. The stock has not
 * been chosen yet, so the common European sizes are offered, plus a size IT
 * typed into Settings → Assets if their stock is none of those.
 *
 * "Start at label" is for a half-used sheet: the first labels are left blank
 * so a sheet with six already peeled off is not wasted.
 *
 * Each label carries the tag in large type, the make and model in small
 * type, and a QR code holding only this application's address and the tag -
 * no serial, no holder - so a photograph of a label gives nothing away.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const A4 = { width: 210, height: 297 };

export default function AssetLabels() {
  const [params] = useSearchParams();
  const ids = params.get('ids') || '';
  // ?filter=1&<the register's filters> prints everything those match (the
  // server applies the same WHERE as the list); a place is ?location=<id>.
  const byFilter = params.get('filter') === '1';
  const [onlyNew, setOnlyNew] = useState(params.get('label') === 'unprinted');
  const [recorded, setRecorded] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [stockId, setStockId] = useState(() => {
    try { return localStorage.getItem('jarvis-label-stock') || 'L7651'; } catch { return 'L7651'; }
  });
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    // Opened with nothing chosen (a bookmark, the address typed): say so
    // here rather than asking the server to say it with a 400.
    if (!ids && !byFilter) { setError('Choose the devices to print labels for'); return; }
    const q = { base: window.location.origin };
    if (byFilter) {
      params.forEach((v, k) => { if (k !== 'ids' && k !== 'base' && k !== 'label') q[k] = v; });
      if (onlyNew) q.label = 'unprinted';
    } else {
      q.ids = ids;
    }
    setData(null);
    axios.get(`${API_BASE}/assets/labels`, { params: q })
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Could not make the labels.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString(), onlyNew]);

  const stocks = useMemo(() => (data?.custom
    ? [...LABEL_STOCK, { id: 'custom', name: `Your own size — ${data.custom.width} × ${data.custom.height} mm`, ...data.custom }]
    : LABEL_STOCK), [data]);
  const stock = stocks.find((s) => s.id === stockId) || stocks[0];

  const choose = (id) => {
    setStockId(id);
    try { localStorage.setItem('jarvis-label-stock', id); } catch { /* a private window */ }
  };

  // The page size the browser prints: A4 for sheets, the label itself for a roll.
  const page = stock.roll ? { width: stock.width, height: stock.height } : A4;
  const perPage = stock.columns * stock.rows;
  // A device with no tag (a personal item, or one not yet kept) has nothing to print.
  const tagged = (data?.labels || []).filter((l) => l.asset_tag);
  // "Only those not printed yet": by the server for a filter print (so the
  // cap counts the right devices), here for a hand-picked list.
  const taggedLabels = !byFilter && onlyNew ? tagged.filter((l) => !l.label_printed_at) : tagged;
  const alreadyPrinted = tagged.length - taggedLabels.length;
  const untagged = (data?.labels || []).length - tagged.length;

  const printNow = () => {
    window.print();
    // The print dialog has been used: remember these labels as printed. The
    // printing itself never waits on this, or depends on it.
    if (data?.tracks_printing && taggedLabels.length) {
      axios.post(`${API_BASE}/assets/labels/printed`, { asset_ids: taggedLabels.map((l) => l.id) })
        .then((r) => setRecorded(r.data?.recorded
          ? `${r.data.recorded} label${r.data.recorded === 1 ? '' : 's'} marked as printed.` : ''))
        .catch(() => setRecorded('Could not note these as printed; the labels themselves are unaffected.'));
    }
  };
  const slots = [...Array(stock.roll ? 0 : Math.min(skip, perPage - 1)).fill(null), ...taggedLabels];
  const pages = [];
  for (let i = 0; i < slots.length; i += perPage) pages.push(slots.slice(i, i + perPage));

  // Scale type to the label: the tag takes about a fifth of the height.
  const qr = Math.max(10, Math.min(stock.height - 3, stock.width * 0.45));
  const tagPt = Math.max(6, Math.min(16, stock.height * 0.32));
  const subPt = Math.max(5, Math.min(9, stock.height * 0.2));

  return (
    <div className="as-print">
      <style>{`@page { size: ${page.width}mm ${page.height}mm; margin: 0; }`}</style>
      <div className="as-print-controls container">
        <p><Link to="/assets">← Assets</Link></p>
        <h1 className="page-title">Print labels</h1>
        {error && <p className="as-error" role="alert">{error}</p>}
        {data && (
          <>
            <p className="as-quiet">
              {`${taggedLabels.length} label${taggedLabels.length === 1 ? '' : 's'}.`}
              {untagged ? <span data-testid="labels-untagged">{` ${untagged} device${untagged === 1 ? ' was' : 's were'} skipped because ${untagged === 1 ? 'it has' : 'they have'} no tag.`}</span> : null}
              {data.capped ? <span data-testid="labels-capped">{` ${data.matching} devices match, but one print holds at most ${data.cap}: these are the first ${data.cap}. Print again with "only those not printed yet" for the rest.`}</span> : null}
              {alreadyPrinted ? ` ${alreadyPrinted} already printed ${alreadyPrinted === 1 ? 'was' : 'were'} left out.` : ''}
              {data.refused ? ` ${data.refused} device${data.refused === 1 ? ' was' : 's were'} left out because you may not change ${data.refused === 1 ? 'it' : 'them'}.` : ''}
              {' '}Print at 100% (actual size), with headers and footers switched off.
            </p>
            <div className="as-inline-form">
              <div className="form-group">
                <label htmlFor="as-stock">Label stock</label>
                <select id="as-stock" value={stock.id} onChange={(e) => choose(e.target.value)}>
                  {stocks.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {!stock.roll && (
                <div className="form-group" style={{ flex: '0 1 9rem' }}>
                  <label htmlFor="as-skip">Start at label</label>
                  <input id="as-skip" type="number" min={1} max={perPage} value={skip + 1}
                    onChange={(e) => setSkip(Math.max(0, Math.min(perPage - 1, (Number(e.target.value) || 1) - 1)))} />
                </div>
              )}
              {data.tracks_printing && (
                <label className="as-check">
                  <input type="checkbox" checked={onlyNew} onChange={(e) => { setOnlyNew(e.target.checked); setRecorded(''); }} />
                  Only those not printed yet
                </label>
              )}
              <button type="button" className="btn btn-primary" onClick={printNow}
                disabled={!taggedLabels.length}>
                Print
              </button>
            </div>
            {recorded && <p className="as-note" data-tone="info" role="status" data-testid="labels-recorded">{recorded}</p>}
          </>
        )}
      </div>

      <div className="as-sheets">
        {pages.map((labels, p) => (
          <div key={p} className="as-sheet" data-roll={String(Boolean(stock.roll))}
            style={{ width: `${page.width}mm`, height: `${page.height}mm` }}>
            {labels.map((l, i) => {
              const col = i % stock.columns;
              const row = Math.floor(i / stock.columns);
              const style = {
                left: `${stock.left + col * (stock.width + stock.gap_x)}mm`,
                top: `${stock.top + row * (stock.height + stock.gap_y)}mm`,
                width: `${stock.width}mm`,
                height: `${stock.height}mm`
              };
              if (!l) return <div key={`skip-${i}`} className="as-label" data-blank="true" style={style} />;
              return (
                <div key={l.id} className="as-label" style={style}>
                  <div className="as-label-qr" style={{ width: `${qr}mm`, height: `${qr}mm` }}
                    // The SVG is made by the server from this application's own
                    // address and the tag, by the qrcode library - never from
                    // anything a person typed beyond the tag, which it encodes
                    // rather than embeds as markup.
                    dangerouslySetInnerHTML={{ __html: l.svg }} />
                  <div className="as-label-text">
                    <div className="as-label-tag" style={{ fontSize: `${tagPt}pt` }}>{l.asset_tag}</div>
                    <div className="as-label-sub" style={{ fontSize: `${subPt}pt` }}>
                      {[l.manufacturer, l.model].filter(Boolean).join(' ') || l.category_name}
                    </div>
                    <div className="as-label-sub" style={{ fontSize: `${Math.max(5, subPt - 1)}pt` }}>NCMB · IT</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
